import React, { useState, useCallback } from 'react';
import { GoogleMap, LoadScript, DirectionsService, DirectionsRenderer } from '@react-google-maps/api';

// --- DTO TANIMLARI ---
// Bu interface'leri dışarıya export ediyoruz ki çağıran sayfa (ShipmentOrderManagement) da kullanabilsin.
export interface RoutePointDTO {
    lat: number;
    lng: number;
    name: string;
}

export interface ShipmentWaypointsDTO {
    origin: RoutePointDTO;
    destination: RoutePointDTO;
    waypoints: RoutePointDTO[];
}

// --- STİL VE AYARLAR ---
const containerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
};

// --- PROPS ---
interface Props {
    origin: RoutePointDTO;
    destination: RoutePointDTO;
    waypoints: RoutePointDTO[];
    // Hesaplama bitince linkleri ve istatistikleri üst bileşene gönderen fonksiyon
    onRouteCalculated: (links: string[], distance: string, duration: string) => void;
}

const ShipmentRouteMap: React.FC<Props> = ({ origin, destination, waypoints, onRouteCalculated }) => {
    const [response, setResponse] = useState<google.maps.DirectionsResult | null>(null);

    // .env dosyasındaki API Key'i alıyoruz
    const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

    const directionsCallback = useCallback((
        result: google.maps.DirectionsResult | null,
        status: google.maps.DirectionsStatus
    ) => {
        if (result !== null && status === 'OK') {
            // State'i güncelle (Haritayı çizmek için)
            setResponse(result);

            // --- 1. İSTATİSTİKLERİ HESAPLA ---
            const route = result.routes[0];
            let totalMeters = 0;
            let totalSeconds = 0;

            // Tüm bacakların (legs) mesafe ve sürelerini topla
            route.legs.forEach(leg => {
                totalMeters += leg.distance?.value || 0;
                totalSeconds += leg.duration?.value || 0;
            });

            const distanceStr = (totalMeters / 1000).toFixed(1) + " km";
            const durationStr = Math.floor(totalSeconds / 60) + " dk";

            // --- 2. OPTİMİZE EDİLMİŞ SIRALAMAYI AL ---
            // Google 'optimizeWaypoints: true' dediğimiz için rotayı değiştirmiş olabilir.
            // waypoint_order dizisi bize yeni sırayı verir (Örn: [2, 0, 1])
            const optimizedOrder = route.waypoint_order;

            // Orijinal waypoint listemizi Google'ın sırasına göre yeniden diziyoruz
            const sortedWaypoints = optimizedOrder.map(index => waypoints[index]);

            // --- 3. ŞOFÖR LİNKLERİNİ OLUŞTUR ---
            const generatedLinks = generateDriverLinks(origin, sortedWaypoints);

            // --- 4. SONUÇLARI ÜST BİLEŞENE GÖNDER ---
            onRouteCalculated(generatedLinks, distanceStr, durationStr);
        } else {
            // Sadece hata durumunda log bas, 'OK' değilse
            if (status !== 'OK') {
                console.error('Rota hesaplanamadı. Hata kodu:', status);
            }
        }
    }, [waypoints, origin, onRouteCalculated]);

    // Google Maps Servisine gidecek waypoint formatı
    const directionsWaypoints = waypoints.map(wp => ({
        location: { lat: wp.lat, lng: wp.lng },
        stopover: true
    }));

    return (
        <LoadScript googleMapsApiKey={API_KEY}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={origin}
                zoom={8}
            >
                {/* Rota Henüz Hesaplanmadıysa Servisi Çalıştır */}
                {!response && (
                    <DirectionsService
                        options={{
                            destination: destination,
                            origin: origin,
                            waypoints: directionsWaypoints,
                            travelMode: 'DRIVING' as google.maps.TravelMode,
                            optimizeWaypoints: true // 🔥 En kısa rotayı bulmak için sıralamayı optimize et
                        }}
                        callback={directionsCallback}
                    />
                )}

                {/* Sonuç Varsa Haritaya Çiz */}
                {response && (
                    <DirectionsRenderer
                        options={{
                            directions: response,
                            polylineOptions: {
                                strokeColor: "#1976D2", // Google Mavisi
                                strokeWeight: 5,
                                strokeOpacity: 0.8
                            }
                        }}
                    />
                )}
            </GoogleMap>
        </LoadScript>
    );
};

// --- YARDIMCI FONKSİYONLAR (Link Generator) ---

// Bu fonksiyon durak listesini alır, 8'erli parçalara böler ve Google Maps Linkleri üretir.
const generateDriverLinks = (origin: RoutePointDTO, sortedWaypoints: RoutePointDTO[]): string[] => {
    const links: string[] = [];
    const MAX_STOPS = 8; // Google URL sınırı (waypoints parametresi için)

    // Eğer durak sayısı sınırın altındaysa tek link üret
    if (sortedWaypoints.length <= MAX_STOPS) {
        links.push(createUrl(origin, origin, sortedWaypoints));
    } else {
        // Sınırı aşıyorsa parçala (Zincirleme Rota)
        for (let i = 0; i < sortedWaypoints.length; i += MAX_STOPS) {
            // Parça Başlangıcı: İlk parça ise Depo, değilse önceki parçanın son durağı
            const chunkOrigin = (i === 0) ? origin : sortedWaypoints[i - 1];

            // Parça Sonu İndeksi
            const endIndex = Math.min(i + MAX_STOPS, sortedWaypoints.length);

            // Bu parçadaki duraklar
            const chunkStops = sortedWaypoints.slice(i, endIndex);

            // Son parti mi?
            const isLastBatch = (endIndex === sortedWaypoints.length);

            // Parça Hedefi: Son partiyse Depo (Origin), değilse bu listenin sonuncusu
            const chunkDest = isLastBatch ? origin : chunkStops[chunkStops.length - 1];

            // Navigasyon Mantığı:
            // Eğer son parti değilse, hedef olarak seçtiğimiz durağı "waypoints" listesinden çıkarıyoruz.
            // Çünkü Google Maps'te bir nokta hem 'destination' hem 'waypoint' olamaz.
            const finalStops = isLastBatch ? chunkStops : chunkStops.slice(0, -1);

            links.push(createUrl(chunkOrigin, chunkDest, finalStops));
        }
    }
    return links;
};

// URL String Oluşturucu Helper
const createUrl = (origin: RoutePointDTO, dest: RoutePointDTO, wps: RoutePointDTO[]) => {
    let url = `https://www.google.com/maps/dir/?api=1`; // Universal Link Formatı
    url += `&origin=${origin.lat},${origin.lng}`;
    url += `&destination=${dest.lat},${dest.lng}`;

    if (wps.length > 0) {
        const wpStr = wps.map(p => `${p.lat},${p.lng}`).join('|');
        url += `&waypoints=${wpStr}`;
    }

    url += `&travelmode=driving`;
    return url;
}

// Performans için memoize ediyoruz
export default React.memo(ShipmentRouteMap);